/* gwd-other.js — donation amounts for gift-with-donation.
   Board band (data-gwd-band): chips only fill the field. Donate is the only
   commit. Exact typed dollars at or above the floor add the Custom variant
   at quantity 1 with _gwd_custom_amount in integer cents (typed $80 -> "8000").
   Nothing snaps up to a listed tier. Quantity is always 1.
   Cart Transform reads the property and sets the fixed price.
   A gift line is included only when data-gwd-gift is set. Only the product
   buy box sets that. The lower band does not free a gift.
   data-gwd-gift-available "false" means the gift product or the selected
   variant cannot be purchased. The buy box is hidden and no gift line is
   added. product:variant-change on the product form hides that box, or
   shows it again with the Donate amount and the selected variant id.
   Cart donations and the lower band never set data-gwd-gift, so a sold-out
   gift does not change those plain donations.
   _gwd_pair is written only when that form sits inside [data-gwd-buybox].
   A chip amount and a typed Other amount on that box share one pair:
   "<giftVariantNumericId>.<32 lowercase hex>" from crypto.getRandomValues.
   The donation line also gets _gwd_gift_variant and _gwd_source "pdp".
   The lower band and every other surface never receive _gwd_pair.

   The sticky add submits #ProductForm. It does not include this donation,
   so it does not write a pair.

   The donation product page still uses the Other reveal (data-gwd-other).
   That path uses the same Custom variant and cents property, with no gift.
   The cart Mission write-in does not use this file. cart-drawer.js adds the
   Support PragerU donation product for that field.
   A commit from [data-cart-donate] is still marked so a cart-bar Custom
   line updates instead of stacking. The free-ship Donate button uses that
   and does not free a product. */
(function (root) {
  'use strict';

  var FORMAT_ERROR = 'Enter a donation amount in dollars.';
  var CUSTOM_ERROR = 'Custom amounts are not available right now.';

  function toCents(raw) {
    var s = String(raw == null ? '' : raw).replace(/[$,\s]/g, '');
    if (!/^\d+(\.\d{1,2})?$/.test(s)) return NaN;
    var bits = s.split('.');
    var dollars = parseInt(bits[0], 10);
    var frac = bits[1] || '0';
    if (frac.length === 1) frac += '0';
    return dollars * 100 + parseInt(frac, 10);
  }

  function classify(raw) {
    var s = String(raw == null ? '' : raw).replace(/[$,\s]/g, '');
    if (!s) return 'empty';
    if (/^\d+\.$/.test(s)) return 'incomplete';
    if (!/^\d+(\.\d{1,2})?$/.test(s)) return 'invalid';
    return 'number';
  }

  function formatDollars(cents) {
    var n = Math.abs(cents);
    var dollars = Math.floor(n / 100);
    var frac = n % 100;
    var text = String(dollars);
    if (frac) text += '.' + (frac < 10 ? '0' : '') + frac;
    return '$' + text;
  }

  function formatField(cents) {
    return formatDollars(cents).slice(1);
  }

  /* 16 bytes become 32 lowercase hex characters. The gift variant id is the
     prefix so the app can match the donation to that variant. */
  function pairToken(giftId) {
    var cryptoObj = root.crypto;
    if (!cryptoObj || typeof cryptoObj.getRandomValues !== 'function') return '';
    var bytes = new Uint8Array(16);
    cryptoObj.getRandomValues(bytes);
    var hex = '';
    for (var i = 0; i < bytes.length; i++) {
      var piece = bytes[i].toString(16);
      if (piece.length < 2) piece = '0' + piece;
      hex += piece;
    }
    return String(giftId) + '.' + hex;
  }

  /* The buy box wrapper is the only surface that may pair. An attribute on
     the lower band, or on any other form, does not. */
  function buyboxSurface(node) {
    return !!(node && node.closest && node.closest('[data-gwd-buybox]'));
  }

  /* A gift is offered only when data-gwd-gift is present. Missing means a
     plain donation (lower band, cart, donation product page). "false" means
     the selected gift cannot ship, so the commit must not run. */
  function giftHost(node) {
    if (!node || !node.hasAttribute) return null;
    if (node.hasAttribute('data-gwd-gift')) return node;
    if (node.closest) return node.closest('[data-gwd-gift]');
    return null;
  }

  function giftUnavailable(node) {
    var host = giftHost(node);
    if (!host) return false;
    return host.getAttribute('data-gwd-gift-available') === 'false';
  }

  function pdpPair(surface, giftId) {
    if (surface !== 'buybox' || !giftId) return '';
    return pairToken(giftId);
  }

  function buildItems(customId, cents, giftId, entry, surface) {
    var token = pdpPair(surface, giftId);
    if (surface === 'buybox' && giftId && !token) return null;
    var donation = {
      id: customId,
      quantity: 1,
      properties: { _gwd_custom_amount: String(cents) }
    };
    if (token) {
      donation.properties._gwd_pair = token;
      donation.properties._gwd_gift_variant = String(giftId);
      donation.properties._gwd_source = 'pdp';
    }
    var items = [donation];
    if (token) {
      items.push({
        id: giftId,
        quantity: 1,
        properties: {
          _gwd_threshold: String(entry),
          _gwd_pair: token
        }
      });
    }
    return items;
  }

  /* Chip match is selection only. Commit always uses Custom + cents when that
     variant exists. A listed tier is the fallback only if Custom is missing
     and the typed cents equal that chip. Pairing is the same for both. */
  function commitItems(customId, cents, chipVariantId, giftId, entry, surface) {
    if (customId) return buildItems(customId, cents, giftId, entry, surface);
    if (!chipVariantId) return null;
    var token = pdpPair(surface, giftId);
    if (surface === 'buybox' && giftId && !token) return null;
    var donation = { id: chipVariantId, quantity: 1 };
    if (token) {
      donation.properties = {
        _gwd_pair: token,
        _gwd_gift_variant: String(giftId),
        _gwd_source: 'pdp'
      };
    }
    var items = [donation];
    if (token) {
      items.push({
        id: giftId,
        quantity: 1,
        properties: {
          _gwd_threshold: String(entry),
          _gwd_pair: token
        }
      });
    }
    return items;
  }

  function bandDecision(raw, floor, chipCents, customId, submitted, donateLabel) {
    var prefix = typeof donateLabel === 'string' ? donateLabel : 'Donate';
    var kind = classify(raw);
    var cents = kind === 'number' ? toCents(raw) : NaN;
    var chipIndex = -1;
    if (kind === 'number' && chipCents) {
      for (var i = 0; i < chipCents.length; i++) {
        if (chipCents[i] === cents) {
          chipIndex = i;
          break;
        }
      }
    }
    if (kind === 'empty' || kind === 'incomplete') {
      var pending = '';
      if (submitted) pending = kind === 'incomplete' ? 'format' : 'min';
      return { ok: false, cents: NaN, chipIndex: -1, label: prefix, error: pending };
    }
    if (kind === 'invalid') {
      return { ok: false, cents: NaN, chipIndex: -1, label: prefix, error: 'format' };
    }
    var label = prefix ? prefix + ' ' + formatDollars(cents) : formatDollars(cents);
    if (cents < floor) {
      return { ok: false, cents: cents, chipIndex: -1, label: label, error: 'min' };
    }
    if (chipIndex < 0 && !customId) {
      return { ok: false, cents: cents, chipIndex: -1, label: label, error: 'custom' };
    }
    return { ok: true, cents: cents, chipIndex: chipIndex, label: label, error: '' };
  }

  root.GwdOther = {
    toCents: toCents,
    classify: classify,
    formatDollars: formatDollars,
    formatField: formatField,
    pairToken: pairToken,
    buyboxSurface: buyboxSurface,
    buildItems: buildItems,
    commitItems: commitItems,
    bandDecision: bandDecision
  };

  if (root.__gwdOtherBound) return;
  root.__gwdOtherBound = true;

  var doc = root.document;
  if (!doc || !doc.addEventListener) return;

  function copyText(node, attr, fallback) {
    if (!node || !node.hasAttribute || !node.hasAttribute(attr)) return fallback;
    return node.getAttribute(attr);
  }

  function errorEl(box) {
    return box.querySelector('[data-gwd-error]') || box.querySelector('[data-gwd-other-error]');
  }

  function showError(box, message) {
    var el = errorEl(box);
    var input = box.querySelector('[data-gwd-amount]') || box.querySelector('[data-gwd-other-input]');
    if (el) {
      el.hidden = false;
      el.textContent = message;
    }
    if (input) input.setAttribute('aria-invalid', 'true');
  }

  function clearError(box) {
    var el = errorEl(box);
    var input = box.querySelector('[data-gwd-amount]') || box.querySelector('[data-gwd-other-input]');
    if (el) {
      el.hidden = true;
      el.textContent = '';
    }
    if (input) input.removeAttribute('aria-invalid');
  }

  /* report false updates the button only. The alert waits for blur or submit
     so a minimum miss is not announced on every key. Cart and the donation
     product page share this. */
  function validate(box, report) {
    var input = box.querySelector('[data-gwd-other-input]');
    var raw = input ? input.value : '';
    if (!String(raw).trim()) {
      clearError(box);
      return false;
    }
    var cents = toCents(raw);
    var floor = parseInt(box.getAttribute('data-gwd-floor'), 10);
    if (!isFinite(cents)) {
      if (report) showError(box, copyText(box, 'data-gwd-format-error', FORMAT_ERROR));
      else clearError(box);
      return false;
    }
    if (cents < floor) {
      if (report) showError(box, box.hasAttribute('data-gwd-min-error') ? box.getAttribute('data-gwd-min-error') : 'Enter at least the minimum donation.');
      else clearError(box);
      return false;
    }
    clearError(box);
    return true;
  }

  function syncAdd(box, report) {
    var add = box.querySelector('[data-gwd-other-add]');
    if (!add) return false;
    var ok = validate(box, !!report);
    var customId = parseInt(box.getAttribute('data-gwd-custom'), 10);
    var input = box.querySelector('[data-gwd-other-input]');
    if (ok && !customId) {
      add.disabled = true;
      add.textContent = copyText(box, 'data-gwd-donate-label', 'Donate');
      if (report) showError(box, copyText(box, 'data-gwd-custom-error', CUSTOM_ERROR));
      return false;
    }
    if (ok && customId) {
      add.disabled = false;
      var otherPrefix = copyText(box, 'data-gwd-donate-label', 'Donate');
      add.textContent = otherPrefix ? otherPrefix + ' ' + formatDollars(toCents(input.value)) : formatDollars(toCents(input.value));
      return true;
    }
    add.disabled = true;
    add.textContent = copyText(box, 'data-gwd-donate-label', 'Donate');
    return false;
  }

  function chipCents(band) {
    var chips = band.querySelectorAll('[data-gwd-chip]');
    var amounts = [];
    for (var i = 0; i < chips.length; i++) {
      amounts.push(parseInt(chips[i].getAttribute('data-gwd-cents'), 10));
    }
    return amounts;
  }

  function paintChips(band, chipIndex) {
    var chips = band.querySelectorAll('[data-gwd-chip]');
    for (var i = 0; i < chips.length; i++) {
      var on = i === chipIndex;
      chips[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      chips[i].classList.toggle('is-selected', on);
    }
  }

  function decisionError(band, code) {
    if (code === 'min') {
      if (band.hasAttribute('data-gwd-min-error')) return band.getAttribute('data-gwd-min-error');
      return 'Enter at least the minimum donation.';
    }
    if (code === 'custom') return copyText(band, 'data-gwd-custom-error', CUSTOM_ERROR);
    if (code === 'format') return copyText(band, 'data-gwd-format-error', FORMAT_ERROR);
    return '';
  }

  /* Hide the whole card, including the heading, chips, Other, and Donate.
     The buy-box wrapper leaves the flex stack so the gap under Add to Cart
     does not double. Showing it again leaves the Donate label to syncBand
     and the pair prefix to the gift id already written on the form. */
  function setGiftBoxHidden(band, hidden) {
    var box = band.closest('[data-gwd-buybox]');
    if (!box) return;
    box.hidden = hidden;
    var wrap = box.closest('.shopify-block');
    if (wrap && wrap.parentElement && wrap.parentElement.classList.contains('product-buybox')) {
      wrap.hidden = hidden;
    }
  }

  function paintGiftUnavailable(band) {
    setGiftBoxHidden(band, true);
    clearError(band);
  }

  function paintGiftAvailable(band) {
    setGiftBoxHidden(band, false);
  }

  function syncBand(band, submitted) {
    if (giftUnavailable(band)) {
      paintGiftUnavailable(band);
      return false;
    }
    if (giftHost(band)) paintGiftAvailable(band);
    var input = band.querySelector('[data-gwd-amount]');
    var add = band.querySelector('[data-gwd-donate]');
    var raw = input ? input.value : '';
    var decision = bandDecision(
      raw,
      parseInt(band.getAttribute('data-gwd-floor'), 10),
      chipCents(band),
      parseInt(band.getAttribute('data-gwd-custom'), 10),
      !!submitted,
      copyText(band, 'data-gwd-donate-label', 'Donate')
    );
    paintChips(band, decision.ok ? decision.chipIndex : -1);
    if (decision.error) showError(band, decisionError(band, decision.error));
    else clearError(band);
    if (add) {
      add.disabled = !decision.ok;
      add.textContent = decision.label;
    }
    return decision.ok;
  }

  function syncPdpUrl() {
    var node = doc.querySelector('[data-gwd-sync-url]');
    if (!node || !root.history || !root.location) return;
    var id = node.getAttribute('data-gwd-sync-url');
    if (!id) return;
    var url = new URL(root.location.href);
    if (url.searchParams.get('variant') === id) return;
    url.searchParams.set('variant', id);
    root.history.replaceState({}, '', url);
  }

  function initBands() {
    var bands = doc.querySelectorAll('[data-gwd-band]');
    for (var i = 0; i < bands.length; i++) syncBand(bands[i], false);
  }

  function dispatchItems(items, source) {
    var carrier = doc.createElement('button');
    carrier.type = 'button';
    carrier.hidden = true;
    carrier.setAttribute('data-gift-donate', JSON.stringify(items));
    if (source && source.closest && source.closest('[data-cart-donate]')) {
      carrier.setAttribute('data-cart-donate-commit', '');
    }
    doc.body.appendChild(carrier);
    carrier.click();
    carrier.remove();
  }

  function commitOther(box) {
    if (giftUnavailable(box)) return;
    if (!box || !syncAdd(box, true)) {
      var missingCustom = box ? parseInt(box.getAttribute('data-gwd-custom'), 10) : 0;
      var otherInput = box && box.querySelector('[data-gwd-other-input]');
      var otherCents = otherInput ? toCents(otherInput.value) : NaN;
      var floor = box ? parseInt(box.getAttribute('data-gwd-floor'), 10) : 0;
      if (box && isFinite(otherCents) && otherCents >= floor && !missingCustom) {
        showError(box, copyText(box, 'data-gwd-custom-error', CUSTOM_ERROR));
      }
      return;
    }
    var typed = toCents(box.querySelector('[data-gwd-other-input]').value);
    var giftIdRaw = box.getAttribute('data-gwd-gift');
    var built = buildItems(
      parseInt(box.getAttribute('data-gwd-custom'), 10),
      typed,
      giftIdRaw ? parseInt(giftIdRaw, 10) : 0,
      box.getAttribute('data-gwd-floor'),
      buyboxSurface(box) ? 'buybox' : ''
    );
    if (!built) {
      showError(box, copyText(box, 'data-gwd-custom-error', CUSTOM_ERROR));
      return;
    }
    dispatchItems(built, box);
  }

  doc.addEventListener('click', function (e) {
    var add = e.target.closest('[data-gwd-other-add]');
    if (add) {
      var holder = add.closest('[data-gwd-other-form]');
      if (holder && holder.tagName !== 'FORM') {
        e.preventDefault();
        commitOther(add.closest('[data-gwd-other]'));
      }
      return;
    }

    var chip = e.target.closest('[data-gwd-chip]');
    if (chip) {
      var band = chip.closest('[data-gwd-band]');
      if (!band || giftUnavailable(band)) return;
      e.preventDefault();
      var cents = parseInt(chip.getAttribute('data-gwd-cents'), 10);
      var input = band.querySelector('[data-gwd-amount]');
      if (input && isFinite(cents)) input.value = formatField(cents);
      syncBand(band, false);
      return;
    }

    var toggle = e.target.closest('[data-gwd-other-toggle]');
    if (!toggle) return;
    var box = toggle.closest('[data-gwd-other]');
    if (!box) return;
    e.preventDefault();
    var form = box.querySelector('[data-gwd-other-form]');
    if (!form) return;
    var willOpen = form.hidden;
    form.hidden = !willOpen;
    toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    if (willOpen) {
      var otherInput = box.querySelector('[data-gwd-other-input]');
      if (otherInput) otherInput.focus();
      if (box.closest('[data-cart-donate]') && form.scrollIntoView) {
        form.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    } else {
      clearError(box);
    }
  });

  doc.addEventListener('input', function (e) {
    var amount = e.target.closest('[data-gwd-amount]');
    if (amount) {
      var band = amount.closest('[data-gwd-band]');
      if (band) syncBand(band, false);
      return;
    }
    var input = e.target.closest('[data-gwd-other-input]');
    if (!input) return;
    var box = input.closest('[data-gwd-other]');
    if (box) syncAdd(box, false);
  });

  doc.addEventListener('focusout', function (e) {
    var input = e.target.closest ? e.target.closest('[data-gwd-other-input]') : null;
    if (!input) return;
    var box = input.closest('[data-gwd-other]');
    if (box) syncAdd(box, true);
  });

  doc.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest ? e.target.closest('[data-gwd-other-input]') : null;
    if (!input) return;
    var holder = input.closest('[data-gwd-other-form]');
    if (!holder || holder.tagName === 'FORM') return;
    e.preventDefault();
    commitOther(input.closest('[data-gwd-other]'));
  });

  doc.addEventListener('submit', function (e) {
    var band = e.target && e.target.closest ? e.target.closest('[data-gwd-band]') : null;
    if (band) {
      e.preventDefault();
      if (giftUnavailable(band) || !syncBand(band, true)) return;
      var cents = toCents(band.querySelector('[data-gwd-amount]').value);
      var customId = parseInt(band.getAttribute('data-gwd-custom'), 10);
      var chip = null;
      var chips = band.querySelectorAll('[data-gwd-chip]');
      for (var i = 0; i < chips.length; i++) {
        if (parseInt(chips[i].getAttribute('data-gwd-cents'), 10) === cents) {
          chip = chips[i];
          break;
        }
      }
      var giftRaw = band.getAttribute('data-gwd-gift');
      var items = commitItems(
        customId,
        cents,
        chip ? parseInt(chip.getAttribute('data-gwd-variant'), 10) : 0,
        giftRaw ? parseInt(giftRaw, 10) : 0,
        band.getAttribute(chip ? 'data-gwd-entry' : 'data-gwd-floor'),
        buyboxSurface(band) ? 'buybox' : ''
      );
      if (!items) {
        showError(band, copyText(band, 'data-gwd-custom-error', CUSTOM_ERROR));
        return;
      }
      dispatchItems(items);
      return;
    }

    var form = e.target && e.target.closest ? e.target.closest('[data-gwd-other-form]') : null;
    if (!form || form.tagName !== 'FORM') return;
    e.preventDefault();
    commitOther(form.closest('[data-gwd-other]'));
  });

  /* undefined: the variant JSON could not be read, so leave the server flag.
     null: the current id is not a variant, so the gift cannot be added. */
  function variantFromForm(form) {
    var idInput = form.querySelector('[data-variant-id]');
    var data = form.querySelector('[data-product-variants]');
    if (!idInput || !data || !data.textContent) return undefined;
    var variants;
    try { variants = JSON.parse(data.textContent); } catch (err) { return undefined; }
    if (!Array.isArray(variants)) return undefined;
    var id = String(idInput.value || '');
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === id) return variants[i];
    }
    return null;
  }

  function applyGiftVariant(form, variant) {
    if (!form || variant === undefined) return;
    var bands = form.querySelectorAll('[data-gwd-band][data-gwd-gift]');
    for (var i = 0; i < bands.length; i++) {
      var band = bands[i];
      var available = !!(variant && variant.available);
      if (variant && variant.id != null) band.setAttribute('data-gwd-gift', String(variant.id));
      band.setAttribute('data-gwd-gift-available', available ? 'true' : 'false');
      syncBand(band, false);
    }
  }

  function syncGiftStock() {
    var forms = doc.querySelectorAll('product-form');
    for (var i = 0; i < forms.length; i++) {
      if (!forms[i].querySelector('[data-gwd-gift]')) continue;
      applyGiftVariant(forms[i], variantFromForm(forms[i]));
    }
  }

  doc.addEventListener('product:variant-change', function (e) {
    var form = e.target && e.target.closest ? e.target.closest('product-form') : null;
    var variant = e.detail ? e.detail.variant : null;
    applyGiftVariant(form, variant || null);
  });

  function boot() {
    syncPdpUrl();
    initBands();
    syncGiftStock();
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();
})(typeof window !== 'undefined' ? window : globalThis);
